import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Truck, ArrowRightLeft, Loader2, AlertTriangle } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, formatCurrency } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// Inter-outlet stock transfers (DrinX → YHC, v1 one-way; migrations
// 0147 + 0148). Multi-line document: search products, add to a picking
// list, enter qty per line, set reason + notes at header level, submit
// once. Server writes header + N line items + N ledger rows + N stock
// decrements in one atomic D1 batch. NEVER touches products.cost_fils
// (WAC) — intra-entity moves aren't a cost event. See
// docs/admin/stock-transfers.md for the full accounting rationale.

interface StockTransfer {
  id: string
  transfer_number: string
  store_id: string
  destination: string
  reason: 'yhc_out_of_stock' | 'event_stocking' | 'wholesale_order_fill' | 'other'
  notes: string | null
  line_count: number
  total_qty: number
  created_by_admin_id: string | null
  created_by_email: string | null
  created_at: string
}

interface StockTransferItem {
  id: string
  stock_transfer_id: string
  product_id: string
  qty: number
  unit_cost_fils: number | null
  product_name: string | null
  product_sku: string | null
  product_barcode: string | null
  created_at: string
}

interface ProductPick {
  id: string
  sku: string
  barcode: string | null
  name: string
  size_ml: number | null
  stock: number
  cost_fils: number
}

const REASON_LABEL: Record<StockTransfer['reason'], string> = {
  yhc_out_of_stock: 'YHC out of stock',
  event_stocking: 'Event stocking',
  wholesale_order_fill: 'Wholesale order fill',
  other: 'Other',
}

export default function StockTransfers() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const [filters, setFilters] = useState<{ reason: string; fromDate: string; toDate: string }>({
    reason: '', fromDate: '', toDate: '',
  })
  const [createOpen, setCreateOpen] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)

  const query = useMemo(() => {
    const q = new URLSearchParams()
    if (filters.reason) q.set('reason', filters.reason)
    if (filters.fromDate) q.set('from_date', filters.fromDate)
    if (filters.toDate) q.set('to_date', filters.toDate)
    return q.toString()
  }, [filters])

  const { data, isLoading } = useQuery<{ transfers: StockTransfer[]; total: number }>({
    queryKey: ['stock-transfers', query],
    queryFn: () => api.get(`/stock-transfers${query ? `?${query}` : ''}`).then(r => r.data),
  })

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Truck size={20} className="text-[#c41e3a]" />
            Stock Transfers
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Record physical stock moving from the DrinX warehouse to YHC. Search products, add them to a transfer list, enter qty per line, submit. Writes a <code>transfer_out</code> row to the stock ledger per line with a cost snapshot — never touches WAC and doesn't show up as revenue / COGS (intra-entity move, not a sale). Inbound YHC → DrinX stays on the Purchase Orders flow.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
          >
            <Plus size={16} />
            New transfer
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Reason">
          <select value={filters.reason} onChange={e => setFilters(f => ({ ...f, reason: e.target.value }))} className={selectCls}>
            <option value="">All</option>
            <option value="yhc_out_of_stock">YHC out of stock</option>
            <option value="event_stocking">Event stocking</option>
            <option value="wholesale_order_fill">Wholesale order fill</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="From date">
          <input type="date" value={filters.fromDate}
            onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))} className={inputCls} />
        </Field>
        <Field label="To date">
          <input type="date" value={filters.toDate}
            onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))} className={inputCls} />
        </Field>
      </div>

      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Transfer #</th>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Destination</th>
              <th className="text-left px-4 py-3">Reason</th>
              <th className="text-right px-4 py-3">Lines</th>
              <th className="text-right px-4 py-3">Total qty</th>
              <th className="text-left px-4 py-3">By</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                <Loader2 size={16} className="inline animate-spin mr-2" /> Loading…
              </td></tr>
            )}
            {!isLoading && (data?.transfers.length ?? 0) === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                No transfers yet.{canManage ? ' Click "New transfer" to record one.' : ''}
              </td></tr>
            )}
            {data?.transfers.map(t => (
              <tr key={t.id}
                  className="border-t border-white/5 cursor-pointer hover:bg-white/5"
                  onClick={() => setOpenId(t.id)}>
                <td className="px-4 py-3 text-sm text-white font-mono">{t.transfer_number}</td>
                <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{formatDateTime(t.created_at)}</td>
                <td className="px-4 py-3 text-sm text-gray-300">{t.destination}</td>
                <td className="px-4 py-3 text-xs text-gray-300">{REASON_LABEL[t.reason]}</td>
                <td className="px-4 py-3 text-sm text-white text-right font-mono">{t.line_count}</td>
                <td className="px-4 py-3 text-sm text-white text-right font-mono">{t.total_qty}</td>
                <td className="px-4 py-3 text-xs text-gray-400 truncate max-w-[12rem]">
                  {t.created_by_email ? t.created_by_email.split('@')[0] : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {createOpen && (
        <NewTransferModal
          onClose={() => setCreateOpen(false)}
        />
      )}

      {openId && (
        <TransferDetailDrawer
          transferId={openId}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  )
}

// New transfer builder. Header (reason + notes; destination fixed to
// YHC in v1) + product-picker typeahead + editable line list. Submits
// as one atomic call.
function NewTransferModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [reason, setReason] = useState<StockTransfer['reason']>('yhc_out_of_stock')
  const [notes, setNotes] = useState('')
  const [search, setSearch] = useState('')
  const [lines, setLines] = useState<Array<{
    product_id: string
    display_name: string
    sku: string
    on_hand: number
    qty: string
  }>>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ transfer_number: string; line_count: number; total_qty: number } | null>(null)

  const { data: productsData } = useQuery<{ products: ProductPick[] }>({
    queryKey: ['transfer-product-picker', search],
    queryFn: async () => {
      const r = await api.get(`/products?search=${encodeURIComponent(search)}&limit=20`)
      return { products: r.data.products as ProductPick[] }
    },
    enabled: search.length > 0,
  })

  const totalQty = useMemo(
    () => lines.reduce((a, l) => a + (parseInt(l.qty || '0', 10) || 0), 0),
    [lines]
  )
  const hasInvalidLine = lines.some(l => {
    const q = parseInt(l.qty || '0', 10)
    return !Number.isFinite(q) || q <= 0 || q > l.on_hand
  })

  function addProduct(p: ProductPick) {
    if (lines.some(l => l.product_id === p.id)) {
      setSearch('')
      return
    }
    if (p.stock <= 0) {
      setError(`${p.name} has 0 on hand — can't transfer.`)
      return
    }
    setLines(prev => [...prev, {
      product_id: p.id,
      display_name: p.size_ml ? `${p.name} · ${p.size_ml}ml` : p.name,
      sku: p.sku,
      on_hand: p.stock,
      qty: '1',
    }])
    setSearch('')
    setError(null)
  }

  async function save() {
    setError(null)
    if (lines.length === 0) { setError('Add at least one product line.'); return }
    for (const l of lines) {
      const q = parseInt(l.qty || '0', 10)
      if (!Number.isFinite(q) || q <= 0) {
        setError(`${l.display_name}: qty must be a positive integer.`); return
      }
      if (q > l.on_hand) {
        setError(`${l.display_name}: only ${l.on_hand} on hand.`); return
      }
    }
    setSaving(true)
    try {
      const res = await api.post('/stock-transfers', {
        reason,
        notes: notes.trim() || undefined,
        lines: lines.map(l => ({ product_id: l.product_id, qty: parseInt(l.qty, 10) })),
      })
      const r = res.data as { ok: boolean; transfer_number: string; line_count: number; total_qty: number }
      setResult({ transfer_number: r.transfer_number, line_count: r.line_count, total_qty: r.total_qty })
      qc.invalidateQueries({ queryKey: ['stock-transfers'] })
      qc.invalidateQueries({ queryKey: ['products'] })
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't submit transfer.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl bg-[#0f0f0f] border border-white/10 rounded-xl p-6 max-h-[92vh] overflow-y-auto space-y-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold flex items-center gap-2">
            <ArrowRightLeft size={18} className="text-[#c41e3a]" />
            New transfer <span className="text-gray-500 font-normal">→ YHC</span>
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>

        {!result && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Reason">
                <select value={reason} onChange={e => setReason(e.target.value as StockTransfer['reason'])} className={selectCls}>
                  <option value="yhc_out_of_stock">YHC out of stock</option>
                  <option value="event_stocking">Event stocking</option>
                  <option value="wholesale_order_fill">Wholesale order fill</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field label="Destination">
                <input value="YHC" disabled className={`${inputCls} opacity-60 cursor-not-allowed`} />
              </Field>
            </div>

            <Field label="Notes (optional)">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value.slice(0, 500))}
                rows={2}
                placeholder="Freeform context — visible in the audit log"
                className={`${inputCls} resize-none`}
              />
              <div className="text-[10px] text-gray-600 text-right mt-0.5">{notes.length}/500</div>
            </Field>

            <div>
              <label className="text-xs text-gray-500">Add product</label>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={`${inputCls} mt-1`}
                placeholder="Search by name, SKU, or barcode…"
                autoFocus
              />
              {search && productsData?.products && productsData.products.length > 0 && (
                <div className="mt-2 border border-white/10 rounded-lg max-h-56 overflow-y-auto">
                  {productsData.products.map(p => (
                    <button
                      key={p.id}
                      onClick={() => addProduct(p)}
                      disabled={p.stock <= 0}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <span className="text-white truncate flex items-center gap-2 min-w-0">
                        <span className="truncate">{p.name}</span>
                        {p.size_ml && (
                          <span className="text-[10px] font-semibold uppercase tracking-wide bg-white/10 text-gray-300 px-1.5 py-0.5 rounded shrink-0">
                            {p.size_ml}ml
                          </span>
                        )}
                      </span>
                      <span className="text-xs shrink-0 flex items-center gap-3">
                        <span className="font-mono text-gray-500">{p.sku}</span>
                        <span className={p.stock <= 0 ? 'text-red-400' : p.stock < 5 ? 'text-yellow-400' : 'text-gray-400'}>
                          {p.stock} on hand
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {search && productsData?.products && productsData.products.length === 0 && (
                <p className="mt-2 text-xs text-gray-500">No products match "{search}".</p>
              )}
            </div>

            {lines.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-white/10 rounded-lg">
                Search for a product above to start building the transfer.
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-2 py-2">Product</th>
                    <th className="text-right px-2 py-2 w-20">On hand</th>
                    <th className="text-right px-2 py-2 w-24">Qty</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, idx) => {
                    const q = parseInt(l.qty || '0', 10)
                    const invalid = !Number.isFinite(q) || q <= 0 || q > l.on_hand
                    return (
                      <tr key={l.product_id} className="border-t border-white/10 align-middle">
                        <td className="px-2 py-2 text-sm text-white">
                          <div>{l.display_name}</div>
                          <div className="text-[10px] text-gray-500 font-mono">{l.sku}</div>
                        </td>
                        <td className="px-2 py-2 text-sm text-gray-400 text-right font-mono">{l.on_hand}</td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min={1}
                            max={l.on_hand}
                            step={1}
                            value={l.qty}
                            onChange={e => setLines(prev => prev.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))}
                            className={`${inputCls} text-right font-mono ${invalid ? 'border-red-500/60' : ''}`}
                          />
                        </td>
                        <td className="px-2 py-2 text-right">
                          <button
                            onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))}
                            className="text-gray-500 hover:text-red-400"
                            title="Remove line"
                          >
                            <X size={16} />
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                  <tr className="border-t border-white/10 bg-white/5">
                    <td colSpan={2} className="px-2 py-2 text-right text-sm font-semibold text-gray-300">Total</td>
                    <td className="px-2 py-2 text-right text-sm font-bold text-white font-mono">{totalQty}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            )}

            <p className="text-[11px] text-gray-500 leading-relaxed">
              Records one <code className="text-gray-400">transfer_out</code> movement per line in the stock ledger with a cost snapshot. Does not affect revenue, COGS, or the weighted-average unit cost — intra-entity moves aren't a sale. All lines commit atomically; if any line races past on-hand, the whole submit rolls back.
            </p>
          </>
        )}

        {result && (
          <div className="rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3 space-y-1">
            <div className="text-sm text-green-300 font-medium">Transfer recorded</div>
            <div className="text-xs text-green-300/80">
              <span className="font-mono">{result.transfer_number}</span> — {result.line_count} line{result.line_count === 1 ? '' : 's'} · {result.total_qty} units to YHC
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300 flex items-start gap-2">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-400 hover:text-white">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={save}
              disabled={saving || lines.length === 0 || hasInvalidLine}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRightLeft size={14} />}
              {saving ? 'Submitting…' : `Transfer ${totalQty > 0 ? totalQty : ''} to YHC`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// Read-only detail view of a past transfer. Reuses the drawer overlay
// pattern the rest of the operations pages use.
function TransferDetailDrawer({ transferId, onClose }: { transferId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery<{ transfer: StockTransfer; items: StockTransferItem[] }>({
    queryKey: ['stock-transfers', transferId],
    queryFn: () => api.get(`/stock-transfers/${transferId}`).then(r => r.data),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl bg-[#0f0f0f] border border-white/10 rounded-xl p-6 max-h-[92vh] overflow-y-auto space-y-4"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold flex items-center gap-2">
            <Truck size={18} className="text-[#c41e3a]" />
            {data?.transfer.transfer_number ?? 'Transfer'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>

        {isLoading || !data ? (
          <div className="p-8 text-center text-sm text-gray-500">
            <Loader2 size={16} className="inline animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <Stat label="Destination" value={data.transfer.destination} />
              <Stat label="Reason" value={REASON_LABEL[data.transfer.reason]} />
              <Stat label="Lines" value={String(data.transfer.line_count)} />
              <Stat label="Total qty" value={String(data.transfer.total_qty)} />
              <Stat label="Submitted" value={formatDateTime(data.transfer.created_at)} />
              <Stat label="By" value={data.transfer.created_by_email ?? '—'} />
            </div>

            {data.transfer.notes && (
              <div className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-500">Notes</div>
                <div className="text-sm text-gray-300 whitespace-pre-wrap">{data.transfer.notes}</div>
              </div>
            )}

            <table className="w-full">
              <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="text-left px-2 py-2">Product</th>
                  <th className="text-left px-2 py-2">SKU</th>
                  <th className="text-right px-2 py-2 w-20">Qty</th>
                  <th className="text-right px-2 py-2 w-28">Unit cost</th>
                  <th className="text-right px-2 py-2 w-28">Line value</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map(it => {
                  const lineValue = (it.unit_cost_fils ?? 0) * it.qty
                  return (
                    <tr key={it.id} className="border-t border-white/10">
                      <td className="px-2 py-2 text-sm text-white">{it.product_name ?? '—'}</td>
                      <td className="px-2 py-2 text-xs font-mono text-gray-400">{it.product_sku ?? '—'}</td>
                      <td className="px-2 py-2 text-sm text-white text-right font-mono">{it.qty}</td>
                      <td className="px-2 py-2 text-xs text-gray-400 text-right font-mono">{it.unit_cost_fils ? formatCurrency(it.unit_cost_fils) : '—'}</td>
                      <td className="px-2 py-2 text-sm text-gray-300 text-right font-mono">{it.unit_cost_fils ? formatCurrency(lineValue) : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <p className="text-[10px] text-gray-500 leading-relaxed">
              Unit cost = <code>products.cost_fils</code> snapshotted at submit time. Line value is for context only — this transfer did NOT affect revenue, COGS, or the WAC on any of these SKUs (intra-entity move).
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-sm text-white truncate" title={value}>{value}</div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-500 block mb-1">{label}</label>
      {children}
    </div>
  )
}

const inputCls = 'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'
const selectCls = inputCls
